import { useCallback, useEffect, useState } from "react";
import {
  fetchFoodWholesaleSourceStatus,
  runFoodWholesaleIngestion,
} from "../../../../api/foodApi";

const FoodWholesaleIngestionPage = ({ onClose }) => {
  const [status, setStatus] = useState(null);
  const [city, setCity] = useState("Medellín");
  const [department, setDepartment] = useState("Antioquia");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [limit, setLimit] = useState(250);
  const [dryRun, setDryRun] = useState(true);
  const [useMock, setUseMock] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const loadStatus = useCallback(async () => {
    try {
      const data = await fetchFoodWholesaleSourceStatus();
      setStatus(data);
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(error.message || "Unable to load SIPSA source status.");
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleRun = async () => {
    try {
      setRunning(true);
      setErrorMessage("");
      const summary = await runFoodWholesaleIngestion({
        sourceKey: "DANE_SIPSA_WHOLESALE",
        city,
        department,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        limit: Number(limit) || 250,
        dryRun,
        useMock,
      });
      setResult(summary);
      if (!dryRun) await loadStatus();
    } catch (error) {
      setErrorMessage(error.message || "Wholesale ingestion failed.");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="admin-page food-wholesale-ingestion-page">
      <header className="admin-page__header">
        <div>
          <p className="admin-page__eyebrow">Food Data</p>
          <h1>SIPSA Wholesale Ingestion</h1>
          <p className="muted">Manual DANE SIPSA wholesale price ingestion (no scheduler).</p>
        </div>
        {onClose ? (
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Close
          </button>
        ) : null}
      </header>

      <section className="panel-card">
        <h2>Configuration status</h2>
        {status ? (
          <ul>
            <li>Health: {status.source?.healthStatus}</li>
            <li>Enabled: {status.source?.enabled ? "Yes" : "No"}</li>
            <li>Endpoint configured: {status.configuration?.endpointConfigured ? "Yes" : "No"}</li>
            <li>Mock mode available: {status.configuration?.useMock ? "Yes" : "No"}</li>
            <li>{status.source?.notes}</li>
          </ul>
        ) : null}
        <p className="muted">
          Set <code>SIPSA_WHOLESALE_ENDPOINT_URL</code> for live data, or use mock mode for local
          development (<code>SIPSA_WHOLESALE_MOCK=true</code>).
        </p>
      </section>

      <section className="panel-card food-basket-calculation-page__controls">
        <div className="food-normalization-page__control-row">
          <label>
            City
            <input value={city} onChange={(e) => setCity(e.target.value)} />
          </label>
          <label>
            Department
            <input value={department} onChange={(e) => setDepartment(e.target.value)} />
          </label>
          <label>
            Date from
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </label>
          <label>
            Date to
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </label>
          <label>
            Limit
            <input
              type="number"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              min={1}
              max={500}
            />
          </label>
          <label className="food-comparison-filters__checkbox">
            <input
              type="checkbox"
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
            />
            Dry run
          </label>
          <label className="food-comparison-filters__checkbox">
            <input
              type="checkbox"
              checked={useMock}
              onChange={(e) => setUseMock(e.target.checked)}
            />
            Use mock SIPSA data
          </label>
          <button
            type="button"
            className="btn btn--primary"
            disabled={running}
            onClick={handleRun}
          >
            {running ? "Running…" : dryRun ? "Dry run" : "Run ingestion"}
          </button>
        </div>
      </section>

      {errorMessage ? <p className="food-dashboard__error">{errorMessage}</p> : null}

      {result ? (
        <pre className="food-normalization-page__summary">{JSON.stringify(result, null, 2)}</pre>
      ) : null}
    </div>
  );
};

export default FoodWholesaleIngestionPage;
